import React from 'react';
import PropTypes from 'prop-types';
import FormFieldLayout from '../FormFieldLayout';
import Label from '../Label';
import Input from '../Input';
import InlineMessage from '../InlineMessage';
import { forgeClassHelper, stateClasses } from '../utils/classes';
import FormError from '../FormError';
import { FormContext } from '../Form/FormContext';

const classes = forgeClassHelper('form-field');

function FormField(props) {
  const {
    className,
    labelWidth,
    nested,
    labelText,
    hideRequiredStyles,
    id,
    name,
    inputAs,
    hintText,
    error,
    errorAlwaysBelow,
    required,
    disabled,
    layout,
    labelAlwaysAbove,
    inputRef,
    ...inputProps
  } = props;

  const InputComponent = inputAs;

  const labelId = `${id}-label`;
  const labelFor = inputAs.disableLabelFor ? undefined : id;

  return (
    <FormContext.Consumer>
      {(context) => {
        let isLabelRequired = required;

        if (isLabelRequired) {
          if (
            context &&
            (context.requiredVariation === 'blueBarWithLegend' || context.requiredVariation === 'allFieldsRequired')
          ) {
            isLabelRequired = false;
          }
          if (hideRequiredStyles) {
            isLabelRequired = false;
          }
        }

        return (
          <FormFieldLayout
            labelWidth={labelWidth}
            nested={nested}
            layout={layout}
            statusExtras={
              errorAlwaysBelow
                ? classes({ element: 'status', modifiers: 'below', removeBase: true }).className
                : undefined
            }
            className={stateClasses({ disabled }, className)}
            labelAlwaysAbove={labelAlwaysAbove}
            labelSlot={
              <Label id={labelId} htmlFor={labelFor} text={labelText} disabled={disabled} required={isLabelRequired} />
            }
            messageSlot={hintText && <InlineMessage text={hintText} disabled={disabled} />}
            statusIndicatorSlot={error && <FormError {...classes('form-error')}>{error}</FormError>}
            inputSlot={
              <InputComponent
                {...inputProps}
                {...classes({ element: 'input' })}
                id={id}
                name={name || id}
                required={required}
                hideRequiredStyles={
                  hideRequiredStyles || (context && context.requiredVariation === 'allFieldsRequired')
                }
                disabled={disabled}
                error={error}
                aria-labelledby={labelId}
                ref={inputRef}
              />
            }
          />
        );
      }}
    </FormContext.Consumer>
  );
}

FormField.defaultProps = {
  inputAs: Input,
  required: false,
};

/**
 * Any props not listed here are passed to the input.
 */
FormField.propTypes = {
  /**
   * Sets the `id` attribute on the input, and is used to associate the label
   * with the input using the `htmlFor`attribute.
   *
   * Will be used as the input's name attribute unless the name prop is provided.
   */
  id: PropTypes.string.isRequired,
  /** Text of the label. */
  labelText: PropTypes.node.isRequired,
  /** Sets the classes on the root element (not the input). */
  className: PropTypes.string,
  /** Adds the relevant Forge state class and passes the prop down to the input */
  disabled: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** Tells the error to always render below the input area instead of based on content-size and breakpoint  */
  errorAlwaysBelow: PropTypes.bool,
  /** Hides any required styling. Can be set manually or by the required field variation set for `Form`. */
  hideRequiredStyles: PropTypes.bool,
  /** Inline message displayed adjacent to the input. */
  hintText: PropTypes.string,
  /** The element type of the input. A React functional or class component (e.g., DateInput). */
  inputAs: PropTypes.elementType,
  /** a ref to the input */
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    /** Note: { current: PropTypes.instanceOf(HTMLInputElement) } would be more
     * more correct, but causes a StackOverflow when building gatsby.
     */
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  /**
   * Affects the vertical space that the field occupies.
   * Uses the 'medium' layout if unspecified.
   * If in a Form, overrides the 'layout' set at the Form level for this field.
   */
  layout: PropTypes.oneOf(['super-compact', 'compact', 'medium', 'large']),
  /** Makes label always go above input. Sets default for all form rows. Overrides `labelWidth`. */
  labelAlwaysAbove: PropTypes.bool,
  /** Width of the label in grid columns. Sets default for all form rows. Defaults to 3/12. */
  labelWidth: PropTypes.number,
  /** Sets the `name` attribute on to the input. Defaults to the value of the `id` prop. */
  name: PropTypes.string,
  /**
   * If true, removes standard grid row outer margins.
   * Set to false if the form is at the top level of page layout.
   * Sets default for all form rows.
   */
  nested: PropTypes.bool,
  /** Passed down to the input*/
  onBlur: PropTypes.func,
  /** Passed down to the input*/
  onChange: PropTypes.func,
  /** Passed down to the input*/
  onFocus: PropTypes.func,
  /** Passed down to the input */
  required: PropTypes.bool,
  /**
   * Changes the outer element from a `<div>` to a `<fieldset>` for use with
   * form components with multiple inputs (e.g., `SelectionList`, `MultiField`).
   * When using this, set the useLegend prop to true on the `Label` component you pass to inputSlot.
   */
  useFieldset: PropTypes.bool,
  /**
   * Passthrough props to the `inputAs` component.
   */
  '...rest': PropTypes.any,
};

export default FormField;
